import { ChangeDetectorRef, Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { AppService } from '../core/config/app.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuModule } from 'headlessui-angular';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgClass, DatePipe } from '@angular/common';
import { SharedModule } from '../shared/shared.module';
import { IconModule } from "../shared/components/icon/icon.module";
import { AdminUserProfileState } from '../core/services/admin-user-profile.state';
import { BrandingService } from '../core/services/branding.service';
import { AuthSessionService } from '../core/services/auth-session.service';
import { ADMIN_PERMISSION_SLUGS } from '../core/constants/admin-permission-slugs';
import { PermissionService } from '../core/services/permission.service';
import { HasPermissionPipe } from '../core/pipes/has-permission.pipe';
import { resolvePageTitle } from '../core/utils/page-title.util';
import { AdminNotificationService, AdminAlertItem } from '../core/services/admin-notification.service';
import { BillingApiService } from '../core/services/billing-api.service';
import { isPostpaidBillingType } from '../core/guards/prepaid-billing.guard';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'header',
    templateUrl: './header.html',
    styleUrls: ['./header.css'],
    standalone: true,
    imports: [
    NgClass,
    AsyncPipe,
    DatePipe,
    RouterLink,
    FormsModule,
    MenuModule,
    TranslateModule,
    SharedModule,
    IconModule,
    HasPermissionPipe,
],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private readonly appSetting = inject(AppService);
    readonly store = this.appSetting.indexState;
    /** Profile from `GET /user/profile` (signals). */
    readonly profileState = inject(AdminUserProfileState);
    /** Tenant logo signal; falls back to static asset when no branding is set. */
    protected readonly branding = inject(BrandingService);
    private readonly authSession = inject(AuthSessionService);
    /** Slugs for `hasPermission` in the horizontal menu / profile dropdown. */
    readonly slugs = ADMIN_PERMISSION_SLUGS;
    readonly permissionService = inject(PermissionService);
    readonly notificationService = inject(AdminNotificationService);
    private readonly billingApi = inject(BillingApiService);
    private readonly cdr = inject(ChangeDetectorRef);

    /** False for POSTPAID tenants — they are billed in arrears, not via subscription plans. */
    showSubscriptionsMenu = true;

    search = false;
    currentPageName: string = 'Dashboard';

    private readonly appTitleSuffix = 'Agentic PD Admin';
    private readonly destroy$ = new Subject<void>();

    /** Observable list of unread alerts (used with async pipe in template). */
    readonly notifications$: Observable<AdminAlertItem[]> = this.notificationService.alerts;
    /** Unread count badge. */
    readonly unreadCount$: Observable<number> = this.notificationService.unreadCount;

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
    ) {}

    ngOnInit() {
        // Initialize page name on component load
        this.updatePageTitle(this.router.url);

        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.updatePageTitle(event.urlAfterRedirects);
                this.cdr.markForCheck();
            }
        });

        // Connect WebSocket and load initial notifications
        this.notificationService.init();

        // Keep nav items in sync when permissions finish loading after login/navigation.
        this.permissionService.userPermissions$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cdr.markForCheck());
        this.permissionService.permissionsRevision$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cdr.markForCheck());

        this.permissionService.ensureLoaded()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cdr.markForCheck());

        this.billingApi.summary()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    this.showSubscriptionsMenu = !isPostpaidBillingType(res?.data?.billing_type);
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.showSubscriptionsMenu = true;
                    this.cdr.markForCheck();
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updatePageTitle(url: string): void {
        this.currentPageName = resolvePageTitle(url);
        document.title = `${this.currentPageName} | ${this.appTitleSuffix}`;
    }

    /** Whether a horizontal nav route is the current page (works on first load after login). */
    isMenuActive(route: string, exact = false): boolean {
        return this.router.isActive(route, {
            paths: exact ? 'exact' : 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
        });
    }

    /** Dismiss a single notification (marks read). */
    dismissNotification(id: number, event: Event): void {
        event.stopPropagation();
        this.notificationService.markRead(id).subscribe();
    }

    /** Mark all notifications read. */
    clearAllNotifications(): void {
        this.notificationService.markAllRead().subscribe();
    }

    changeLanguage(item: any) {
        this.translate.use(item.code);
        this.appSetting.toggleLanguage(item);
        if (this.store()?.locale?.toLowerCase() === 'ae') {
            this.storeData.dispatch({type: 'toggleRTL', payload: 'rtl'});
        } else {
            this.storeData.dispatch({type: 'toggleRTL', payload: 'ltr'});
        }
        globalThis.location.reload();
    }

    signOut(): void {
        this.authSession.signOut();
    }

    /** System Preferences — roles, SMTP, organization (tenant admin). */
    get canAccessSystemPreferences(): boolean {
        this.permissionService.permissionRevision();
        return this.permissionService.hasAnyPermission([
            this.slugs.ROLE_LIST,
            this.slugs.SMTP_SETTINGS_LIST,
            this.slugs.SMTP_SETTINGS_UPDATE,
            this.slugs.ORGANIZATION_DETAILS_LIST,
            this.slugs.ORGANIZATION_DETAILS_UPDATE,
        ]);
    }
}
